import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/mocks/anime";

interface HeroCarouselProps {
  items: Anime[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="w-full h-[320px] md:h-[480px] rounded-lg bg-background-100 flex items-center justify-center text-foreground-500">
        暂无推荐内容，请稍后刷新
      </div>
    );
  }

  const current = items[active];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] rounded-lg overflow-hidden bg-foreground-950">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            idx === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.banner || item.cover}
            alt={`${item.title} 焦点图`}
            title={`${item.title} 动漫在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/25"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/60 to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex flex-col justify-end px-6 md:px-14 pb-10 md:pb-16">
        <div key={current.id} className="max-w-2xl animate-fadeUp">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-semibold">
              焦点推荐
            </span>
            {current.categories.map((c) => (
              <span
                key={c}
                className="px-2.5 py-1 rounded-full bg-background-50/20 text-background-50 text-xs backdrop-blur-sm border border-background-50/25"
              >
                {c}
              </span>
            ))}
            <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
              {current.rating.toFixed(1)} 分
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-background-50 mb-3 md:mb-4">
            {current.title}
          </h2>
          <p className="text-sm md:text-base text-background-100/90 leading-relaxed line-clamp-2 md:line-clamp-3 mb-6">
            {current.description}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/category?category=${encodeURIComponent(current.categories[0])}`}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold backdrop-blur-sm border border-background-50/30 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-compass-3-line text-lg"></i>
              查看同类
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-4 right-4 md:right-14 flex items-center gap-2">
        <button
          type="button"
          aria-label="上一个"
          onClick={() => setActive((prev) => (prev - 1 + items.length) % items.length)}
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm border border-background-50/30 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一个"
          onClick={() => setActive((prev) => (prev + 1) % items.length)}
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm border border-background-50/30 flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute z-20 bottom-6 left-6 md:left-14 flex items-center gap-2">
        {items.map((item, idx) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${idx + 1} 张`}
            onClick={() => setActive(idx)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              idx === active ? "w-8 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}